/** @jsxImportSource ./simboluri-jsx */
// VEDEREA COTATĂ A STÂLPULUI.
//
// Desenul care lipsea: elevația stâlpului cu tot ce se vede pe o planșă și nu
// se vedea în viewer — placa, banda grinzii la nod, înălțimea liberă separată
// de cea de nivel, zonele critice, ÎNNĂDIREA barelor și fundația la adâncimea
// ei REALĂ.
//
// Se desenează fie un singur nivel (implicit — omul se uită la armarea unui
// nivel, nu la toată stiva), fie stiva întreagă cu fundația dedesubt.
//
// Ce NU face: nu calculează nimic. Toate cotele vin din rezultatul Pasului 15
// (`h_liber_m`, `h_grinda_nod_m`, `h_placa_m`, `l_0_m`) și din Pasul 6
// (adâncimile și secțiunea fundației). Pe rulările de dinainte de 26.08, care
// nu poartă câmpurile noi, se cade pe estimări MARCATE ca atare — un desen cu
// cote inventate în silență ar fi mai rău decât niciun desen.

import { fundatiaAdoptata } from "./fundatia";
import { useT, useTx } from "../i18n";
import { NivelStalp, RezistentaResult, StepState } from "./tipuri";
import { CotaV, CotaH, HasuraPlaca, LinieTeren, LegendaCote, mFmt } from "./cote";
import { PAL } from "./paletaDesen";

// ---------------------------------------------------------------------------
// Fundația de sub un stâlp anume
// ---------------------------------------------------------------------------

export type FundatieStalp = {
  // adâncimea ȘANȚULUI, sub pardoseala celui mai de jos nivel (D'_f)
  Df_m: number;
  // adâncimea de la terenul sistematizat (D_f)
  Dext_m: number;
  hPardoseala_m: number;
  // umplutura de deasupra betonului, direct sub pardoseală: betonul NU începe
  // de la fața de jos a plăcii
  hPeste_m: number;
  // înălțimea secțiunii de BETON (talpă + inimă)
  Hc_m: number;
  B_m: number;
  hTalpa_m: number;
  bInima_m: number;
  hInima_m: number;
  ax: string | null;
  areSubsol: boolean;
  // lățimea vine de la axul stâlpului sau doar din secțiunea globală?
  bLocal: boolean;
};

/** Geometria fundației de sub stâlpul `ref`, din rezultatul Pasului 6.
 *  `null` = pasul n-a rulat, deci desenul NU inventează o talpă.
 *
 *  ADÂNCIMEA se citește din `sectiune_fundatie` / `adancime_m`, NU din
 *  `verificari.adancimi`. Motivul e măsurat pe proiectul 2:
 *  acolo `adancime_m` = 3,00 m și secțiunea e coerentă cu el
 *  (H_c 2,70 + h_peste 0,30 = 3,00), dar `adancimi` poartă încă
 *  D_int = 1,45 m — adâncimea MINIMĂ, de la care a plecat căutarea. Cine
 *  desenează talpa după `adancimi` o pune la mai puțin de jumătate din
 *  adâncimea reală, adică exact greșeala pentru care s-a cerut reparația.
 *  Din `adancimi` / `cota_fundare` se ia numai cât e pardoseala sub teren,
 *  care nu depinde de căutare. */
export function fundatiaStalpului(
  steps: Record<string, StepState>,
  ref: string
): FundatieStalp | null {
  const f = fundatiaAdoptata(steps) as RezistentaResult | null;
  const sec = f?.sectiune_fundatie;
  if (!f || !sec) return null;
  const stem = ref.split(".")[0];

  const Df = sec.Df_m || f.adancime_m || 0;
  if (!(Df > 0)) return null;
  const Hc = sec.H_m || Df;
  const hPeste = sec.h_peste_m ?? Math.max(Df - Hc, 0);

  // lățimea CHIAR SUB stâlpul ăsta: rândul lui din verificări, altfel axul
  // pe care stă, altfel secțiunea globală
  const st = (f.verificari?.stalpi ?? []).find(
    (x) => (x.id_plan ?? "").split(".")[0] === stem
  );
  const ax = st?.ax ?? null;
  const axRow = ax
    ? (f.verificari?.axe ?? []).find((a) => a.nume === ax)
    : undefined;
  const bLocal = st?.B_aici_m != null || axRow?.B_m != null;
  const B = st?.B_aici_m ?? axRow?.B_m ?? sec.B_m;

  // talpa se îngroașă cu lățimea (consola e mai lungă), deci pe un ax lățit
  // local h_talpa al secțiunii GLOBALE e o subestimare. Se reface exact ca în
  // SF.geometrie: cerută de consolă, dar RETEZATĂ la înălțimea secțiunii —
  // mai groasă n-are unde.
  const hT =
    B > sec.B_m + 1e-6
      ? Math.min(Math.max(sec.h_talpa_m, 0.3, (B - sec.b_inima_m) / 2), Hc)
      : sec.h_talpa_m;
  const hI = Math.max(Hc - hT, 0);
  const hPard =
    f.cota_fundare?.h_pardoseala_sub_teren_m ??
    f.adancimi?.h_pardoseala_sub_teren_m ??
    0;
  return {
    Df_m: Df,
    Dext_m: Df + hPard,
    hPardoseala_m: hPard,
    hPeste_m: hPeste,
    Hc_m: Hc,
    B_m: B,
    hTalpa_m: hT,
    bInima_m: sec.b_inima_m,
    hInima_m: hI,
    ax,
    areSubsol: Boolean(f.cota_fundare?.are_subsol ?? f.adancimi?.are_subsol),
    bLocal,
  };
}

// ---------------------------------------------------------------------------
// Geometria de desen a unui nivel, cu marcajul „estimat"
// ---------------------------------------------------------------------------

type Geo = {
  n: NivelStalp;
  z0: number;
  z1: number;
  H: number;
  hG: number;      // banda planșeului la nodul de sus
  hPl: number;     // din care placă
  hLib: number;    // înălțimea liberă a stâlpului
  l0: number;      // petrecerea
  zPet: number;    // cota ei deasupra plăcii nivelului
  petInCritica: boolean;   // n-a încăput deasupra zonei critice
  arePet: boolean;         // bara TRECE prin nivel, fără să se petreacă
  lcr: number;     // zona critică de la BAZA nivelului — (5.22)
  lcrSus: number;  // cea de sub nod, mai scurtă — (5.23)
  estimat: boolean;
};

export function geoNiveluri(toate: NivelStalp[]): Geo[] {
  let z = 0;
  const out: Geo[] = [];
  for (const n of toate) {
    const H = n.H_m;
    // rulările vechi n-au câmpurile de desen: se estimează și se SPUNE
    const estimat = n.h_grinda_nod_m == null;
    const hPl = n.h_placa_m ?? 0.15;
    const hG = n.h_grinda_nod_m ?? Math.max(hPl, Math.min(0.6, H / 5));
    const hLib = n.h_liber_m ?? Math.max(H - hG, 0.05);
    // fără l_0 din pas, reperul EC2 pentru α₆ = 1,5 e ≈ 45·Ø la C25/30
    const l0 = n.petrecere?.l0_m ?? n.l_0_m ?? (45 * n.armare.phi) / 1000;
    // COTA petrecerii o decide Pasul 15, nu desenul: acolo se știe dacă în
    // fereastra dintre cele două zone critice încape l₀, dacă nu încape că
    // l₀ poartă k_s = 1,25 (P100-1 §5.7.3(4)) — și dacă nivelul are petrecere
    // deloc: o bară care ține două etaje TRECE prin cel din mijloc
    const zPet = n.petrecere?.z_m ?? n.armare.l_cr_m;
    out.push({
      n, z0: z, z1: z + H, H, hG, hPl, hLib, l0, zPet,
      petInCritica: !!n.petrecere?.in_zona_critica,
      arePet: n.petrecere !== null,
      lcr: n.armare.l_cr_jos_m ?? n.armare.l_cr_m,
      lcrSus: n.armare.l_cr_sus_m ?? n.armare.l_cr_m, estimat,
    });
    // cotele sunt ABSOLUTE pe verticală: fără acumulare, toate nivelurile s-ar
    // desena unul peste altul (măsurat pe ST1 al proiectului 2 —
    // patru niveluri suprapuse la z = 0, cu cotele lor una peste alta)
    z += H;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Desenul
// ---------------------------------------------------------------------------

const COL = {
  beton: "#ececec",
  betonLinie: "#737373",
  placa: "#dbdfe4",
  placaLinie: "#8f9298",
  grinda: "#e1e1e1",
  grindaLinie: "#929292",
  critica: "#f5ecd8",
  lap: "#f7e2e4",
  lapLinie: "#e8bcc2",
  bara: "#b5323d",
  etrier: PAL.etrieri,
  musta: "#a97614",
  acc: "#0b7f92",
};

export default function VedereStalpCotata({
  toate,
  selectate,
  fundatie,
  cuFundatie,
  cCm,
  latime = 520,
  inaltimeMax = 520,
}: {
  // stiva ÎNTREAGĂ (dă cotele absolute pe verticală)
  toate: NivelStalp[];
  // nivelurile DESENATE (o felie contiguă din stivă)
  selectate: NivelStalp[];
  fundatie?: FundatieStalp | null;
  cuFundatie: boolean;
  cCm: number;
  latime?: number;
  inaltimeMax?: number;
}) {
  const t = useT();
  const tx = useTx();
  const geo = geoNiveruriSelectate(toate, selectate);
  if (!geo.length) {
    return (
      <div className="sg-muted" style={{ fontSize: 12 }}>
        {t("struct.dsc.fara_nivel")}
      </div>
    );
  }
  const unul = geo.length === 1;
  const fnd = cuFundatie ? fundatie ?? null : null;

  // extinderea pe verticală, în metri
  const zSusGeo = geo[geo.length - 1].z1;
  const zJosGeo = fnd ? -fnd.Df_m : geo[0].z0 - (unul ? 0.34 : 0);
  // aer deasupra: barele înnădite ies din ultimul planșeu
  const sus = zSusGeo + (unul ? 0.34 : 0.3);
  const jos = zJosGeo - 0.2;
  const extent = Math.max(sus - jos, 0.5);
  // sub talpă intră cota B, cu textul ei — de aici plinta mai mare la
  // desenele cu fundație
  const padT = 20, padB = fnd ? 50 : 34;
  // scara: umple caseta, dar nu peste 100 px/m (un nivel singur) și nu sub 9
  const pxm = Math.max(9, Math.min(unul ? 100 : 46, inaltimeMax / extent));
  const H = Math.round(extent * pxm) + padT + padB;
  const W = latime;
  // axa stâlpului: la stânga cotele (până la 4 raiuri când e fundație cu
  // subsol), la dreapta etichetele
  const ax = Math.round(W * (fnd ? 0.62 : 0.46));
  const Y = (m: number) => padT + (sus - m) * pxm;
  const X = (m: number) => ax + m * pxm;
  // cât iese placa/grinda în afara stâlpului, ca să se citească banda
  const ext = Math.min(0.9, (W * 0.34) / pxm / 2);
  // etrierii se desenează doar când pasul lor e vizibil
  const cuEtrieri = pxm >= 20;

  const cote: React.ReactNode[] = [];
  const corp: React.ReactNode[] = [];
  const armat: React.ReactNode[] = [];
  const etich: React.ReactNode[] = [];
  let estimat = false;
  let coteSarite = false;

  geo.forEach((g, i) => {
    const n = g.n;
    if (g.estimat) estimat = true;
    const hw = n.h_cm / 200;
    const zNod = g.z1 - g.hG;      // fața de jos a grinzii
    // nod FĂRĂ grindă (stâlpul nu prinde nimic acolo): banda planșeului e
    // chiar placa, `_banda_nod` cade pe grosimea ei. Atunci nu există bandă
    // de grindă de desenat, iar cele două cote ar cădea una peste alta —
    // măsurat pe ST100.1 al proiectului 1.
    const faraGrinda = g.hG - g.hPl < 0.02;
    const cb = cCm / 100;
    const key = `n${i}`;

    // ---- betonul: corpul liber + nodul ------------------------------------
    corp.push(
      <rect key={`${key}c`} x={X(-hw)} y={Y(zNod)}
            width={X(hw) - X(-hw)} height={Y(g.z0) - Y(zNod)}
            fill={COL.beton} stroke={COL.betonLinie} strokeWidth={1} />
    );
    // zonele critice l_cr: la baza nivelului și sub nod. Se retează la
    // înălțimea liberă — pe un nivel scund (sau cu l_cr mare) cele două
    // s-ar suprapune și ar ieși din stâlp.
    const kSus = Math.min(g.z0 + g.lcr, zNod);
    const kJos = Math.max(zNod - g.lcrSus, g.z0);
    corp.push(
      <rect key={`${key}k1`} x={X(-hw)} y={Y(kSus)}
            width={X(hw) - X(-hw)} height={Math.max(Y(g.z0) - Y(kSus), 0)}
            fill={COL.critica} />
    );
    corp.push(
      <rect key={`${key}k2`} x={X(-hw)} y={Y(zNod)}
            width={X(hw) - X(-hw)} height={Math.max(Y(kJos) - Y(zNod), 0)}
            fill={COL.critica} />
    );
    // banda grinzii (fără placă) + placa deasupra ei
    if (!faraGrinda) {
      corp.push(
        <rect key={`${key}g`} x={X(-ext)} y={Y(g.z1 - g.hPl)}
              width={X(ext) - X(-ext)} height={Y(zNod) - Y(g.z1 - g.hPl)}
              fill={COL.grinda} stroke={COL.grindaLinie} strokeWidth={0.9} />
      );
    }
    corp.push(
      <rect key={`${key}n`} x={X(-hw)} y={Y(g.z1)}
            width={X(hw) - X(-hw)} height={Y(zNod) - Y(g.z1)}
            fill={COL.beton} stroke={COL.betonLinie} strokeWidth={1} />
    );
    corp.push(
      <rect key={`${key}p`} x={X(-ext)} y={Y(g.z1)}
            width={X(ext) - X(-ext)} height={Y(g.z1 - g.hPl) - Y(g.z1)}
            fill={COL.placa} stroke={COL.placaLinie} strokeWidth={0.9} />
    );
    corp.push(
      <HasuraPlaca key={`${key}h`} x={X(-ext)} y={Y(g.z1)}
                   w={X(ext) - X(-ext)} h={Y(g.z1 - g.hPl) - Y(g.z1)} />
    );

    // ---- etrierii ---------------------------------------------------------
    if (cuEtrieri) {
      const pasCr = Math.max(n.armare.etrieri.pas_critic_cm, 4) / 100;
      const pasCu = Math.max(n.armare.etrieri.pas_curent_cm, 4) / 100;
      const linii: number[] = [];
      let z = g.z0 + 0.05;
      let guard = 0;
      while (z < g.z1 - 0.03 && guard < 400) {
        linii.push(z);
        const critic =
          z - g.z0 < g.lcr || zNod - z < g.lcr || z > zNod;
        z += critic ? pasCr : pasCu;
        guard++;
      }
      armat.push(
        <path key={`${key}e`}
              d={linii.map((z) =>
                `M ${X(-hw + cb)},${Y(z)} L ${X(hw - cb)},${Y(z)}`).join(" ")}
              fill="none" stroke={COL.etrier} strokeWidth={0.75} />
      );
    }

    // ---- barele longitudinale + PETRECEREA --------------------------------
    // Cota petrecerii vine de la Pasul 15: deasupra zonei critice acolo unde
    // încape (P100-1 §5.7.1(2) o vrea în afara ei), la baza nivelului acolo
    // unde nu — și atunci banda e chihlimbarie, ca să se vadă că s-a plătit
    // ceva: l₀ poartă k_s = 1,25 (§5.7.3(4)). Un nivel prin care bara doar
    // TRECE (bucata ține două etaje) n-are nici bandă, nici a doua bară.
    const zl0 = Math.min(g.z0 + g.zPet, Math.max(zNod - g.l0, g.z0));
    const zl1 = Math.min(zl0 + g.l0, zNod);
    if (g.arePet) {
      armat.push(
        <rect key={`${key}lz`} x={X(-hw) - 3} y={Y(zl1)}
              width={X(hw) - X(-hw) + 6} height={Y(zl0) - Y(zl1)}
              fill={g.petInCritica ? COL.critica : COL.lap}
              stroke={g.petInCritica ? COL.musta : COL.lapLinie}
              strokeWidth={0.8} strokeDasharray={g.petInCritica ? "3 2" : undefined} />
      );
    }
    const ultim = i === geo.length - 1;
    for (const sgn of [-1, 1]) {
      const xb = X(sgn * (hw - cb));
      armat.push(
        <line key={`${key}b${sgn}`} x1={xb} y1={Y(g.z0)} x2={xb}
              y2={Y(g.z1 + (ultim ? 0.16 : 0))}
              stroke={COL.bara} strokeWidth={1.5} />
      );
      // bara petrecută, alături — numai unde chiar e o petrecere
      if (g.arePet) {
        armat.push(
          <line key={`${key}l${sgn}`} x1={xb + sgn * 3} y1={Y(zl0)}
                x2={xb + sgn * 3} y2={Y(zl1)}
                stroke={COL.bara} strokeWidth={1.5} />
        );
      }
    }

    // ---- cotele ----------------------------------------------------------
    // Cotele per nivel se pun doar dacă textul rotit ÎNCAPE în înălțimea
    // nivelului. La o stivă înaltă (blocul de 10 niveluri al proiectului 17
    // are ~30 m) etichetele vecine s-ar călca; atunci rămâne cota totală, iar
    // înălțimile stau în tabelul de sub desen.
    const incapCote = Y(g.z0) - Y(g.z1) >= 48;
    if (incapCote) {
      // rail 1 (lipit de stâlp): înălțimea de nivel, ax-planșeu → ax-planșeu
      cote.push(
        <CotaV key={`${key}H`} x={X(-hw) - 24} y1={Y(g.z1)} y2={Y(g.z0)}
               martorX={X(-hw)} text={`H ${mFmt(g.H)}`} />
      );
      // rail dreapta: înălțimea LIBERĂ — mărimea despre care e desenul
      cote.push(
        <CotaV key={`${key}L`} x={X(hw) + 24} y1={Y(zNod)} y2={Y(g.z0)}
               martorX={X(hw)} text={`liber ${mFmt(g.hLib)}`}
               culoare={COL.acc} laDreapta />
      );
    } else {
      coteSarite = true;
    }
    if (unul) {
      // grinda (cotă lungă, text rotit) pe raiul interior; placa (cotă
      // scurtă, text orizontal) pe cel exterior, ca textul să aibă loc la
      // dreapta lui fără să taie raiul vecin
      if (faraGrinda) {
        // o singură cotă: banda ESTE placa
        cote.push(
          <CotaV key={`${key}pl`} x={X(ext) + 26} y1={Y(g.z1)}
                 y2={Y(g.z1 - g.hPl)} martorX={X(ext)}
                 text={t("struct.dsc.placa", { n: mFmt(g.hPl) })} laDreapta />
        );
      } else {
        cote.push(
          <CotaV key={`${key}gr`} x={X(ext) + 26} y1={Y(g.z1)} y2={Y(zNod)}
                 martorX={X(ext)} text={t("struct.dsc.grinda", { n: mFmt(g.hG) })} laDreapta />
        );
        cote.push(
          <CotaV key={`${key}pl`} x={X(ext) + 68} y1={Y(g.z1)}
                 y2={Y(g.z1 - g.hPl)} martorX={X(ext)}
                 text={t("struct.dsc.placa", { n: mFmt(g.hPl) })} laDreapta />
        );
      }
      cote.push(
        <CotaV key={`${key}l0`} x={X(-hw) - 62} y1={Y(zl1)} y2={Y(zl0)}
               martorX={X(-hw)} text={`l₀ ${mFmt(g.l0)}`} culoare={COL.bara} />
      );
      cote.push(
        <CotaV key={`${key}lc`} x={X(-hw) - 100} y1={Y(g.z0 + g.lcr)}
               y2={Y(g.z0)} martorX={X(-hw)} text={`l_cr ${mFmt(g.lcr)}`} />
      );
      cote.push(
        <CotaH key={`${key}h`} y={Y(g.z0) + 26} x1={X(-hw)} x2={X(hw)}
               martorY={Y(g.z0)}
               text={t("struct.dsc.h_b", { h: n.h_cm, b: n.b_cm })} />
      );
    } else {
      // pe stivă, eticheta nivelului stă în afara benzii. La un singur nivel
      // NU se pune: dublează titlul cardului și se bate cu raiurile de cotă.
      if (Y(g.z0) - Y(g.z1) >= 22) {
        etich.push(
          <text key={`${key}t2`} x={Math.max(X(ext), X(hw) + 24) + 24}
                y={Y((g.z0 + zNod) / 2) + 3}
                fontFamily="var(--mono)" fontSize={9.5} fill="var(--mut)">
            {n.id_plan}  {n.h_cm}×{n.b_cm}  {n.armare.n}Ø{n.armare.phi}
          </text>
        );
      }
    }
  });

  // ---- fundația, la adâncimea ei reală -----------------------------------
  if (fnd) {
    // betonul NU pornește de la fața de jos a plăcii: deasupra lui stă
    // umplutura de h_peste. Sub ea vine inima, apoi talpa.
    const zSusBeton = -fnd.hPeste_m;
    const zInima = zSusBeton - fnd.hInima_m;
    corp.push(
      <rect key="fi" x={X(-fnd.bInima_m / 2)} y={Y(zSusBeton)}
            width={X(fnd.bInima_m / 2) - X(-fnd.bInima_m / 2)}
            height={Y(zInima) - Y(zSusBeton)}
            fill={COL.beton} stroke={COL.betonLinie} strokeWidth={1} />
    );
    corp.push(
      <rect key="ft" x={X(-fnd.B_m / 2)} y={Y(zInima)}
            width={X(fnd.B_m / 2) - X(-fnd.B_m / 2)}
            height={Y(-fnd.Df_m) - Y(zInima)}
            fill={COL.beton} stroke={COL.betonLinie} strokeWidth={1} />
    );
    corp.push(
      <LinieTeren key="teren" x1={X(-fnd.B_m / 2) - 8} x2={W - 8} y={Y(0)} />
    );
    etich.push(
      <text key="tert" x={W - 10} y={Y(0) + 14} textAnchor="end"
            fontFamily="var(--mono)" fontSize={9.5} fill="var(--mut)">
        {fnd.areSubsol ? "pardoseala subsolului" : "teren ±0,00"}
      </text>
    );
    // mustățile: ancorate în talpă cu cioc, înnădite cu bara stâlpului
    const g0 = geo[0];
    const hw0 = g0.n.h_cm / 200;
    const cb0 = cCm / 100;
    for (const sgn of [-1, 1]) {
      const xb = X(sgn * (hw0 - cb0)) - sgn * 3;
      armat.push(
        <path key={`m${sgn}`}
              d={`M ${X(sgn * (hw0 - cb0 + 0.15))},${Y(-fnd.Df_m + 0.05)} ` +
                 `L ${xb},${Y(-fnd.Df_m + 0.05)} ` +
                 `L ${xb},${Y(g0.z0 + g0.lcr + g0.l0)}`}
              fill="none" stroke={COL.musta} strokeWidth={1.6} />
      );
    }
    cote.push(
      <CotaV key="fdf" x={X(-fnd.B_m / 2) - 24} y1={Y(0)} y2={Y(-fnd.Df_m)}
             martorX={X(-fnd.B_m / 2)}
             text={`D${fnd.areSubsol ? "′" : ""}_f ${mFmt(fnd.Df_m)}`}
             culoare={COL.acc} />
    );
    cote.push(
      <CotaV key="fhc" x={X(fnd.B_m / 2) + 24} y1={Y(zSusBeton)}
             y2={Y(-fnd.Df_m)} martorX={X(fnd.B_m / 2)}
             text={`H_c ${mFmt(fnd.Hc_m)}`} laDreapta />
    );
    cote.push(
      <CotaV key="fht" x={X(fnd.B_m / 2) + 62} y1={Y(zInima)}
             y2={Y(-fnd.Df_m)} martorX={X(fnd.B_m / 2)}
             text={t("struct.dsc.talpa", { n: mFmt(fnd.hTalpa_m) })} laDreapta size={9.5} />
    );
    if (fnd.hPeste_m > 0.01) {
      cote.push(
        <CotaV key="fhp" x={X(-fnd.B_m / 2) - (fnd.areSubsol ? 176 : 138)}
               y1={Y(0)} y2={Y(zSusBeton)}
               martorX={X(-fnd.bInima_m / 2)}
               text={t("struct.dsc.umplutura", { n: mFmt(fnd.hPeste_m) })} size={9} />
      );
    }
    cote.push(
      <CotaH key="fb" y={Y(-fnd.Df_m) + 26} x1={X(-fnd.B_m / 2)}
             x2={X(fnd.B_m / 2)} martorY={Y(-fnd.Df_m)}
             text={`B = ${mFmt(fnd.B_m)} m`} />
    );
    if (fnd.areSubsol) {
      cote.push(
        <CotaV key="fde" x={X(-fnd.B_m / 2) - 62} y1={Y(fnd.hPardoseala_m)}
               y2={Y(-fnd.Df_m)} text={`D_f ${mFmt(fnd.Dext_m)}`} />
      );
    }
    cote.push(
      <CotaV key="tot" x={X(-fnd.B_m / 2) - (fnd.areSubsol ? 100 : 62)}
             y1={Y(zSusGeo)} y2={Y(-fnd.Df_m)}
             text={`total ${mFmt(fnd.Df_m + zSusGeo)}`} />
    );
  }

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", maxWidth: W, background: "#fff", display: "block" }}>
        {corp}
        {armat}
        {cote}
        {etich}
      </svg>
      <div style={{ paddingTop: 8 }}>
        <LegendaCote randuri={[
          [COL.beton, t("struct.dsc.leg_beton")],
          [COL.placa, geo[0]
            ? t("struct.dsc.leg_placa_m", { n: mFmt(geo[0].hPl) })
            : t("struct.dsc.leg_placa")],
          ...(geo.some((g) => g.hG - g.hPl >= 0.02)
            ? [[COL.grinda, t("struct.dsc.leg_grinda")] as [string, string]]
            : []),
          [COL.critica, t("struct.dsc.leg_critica")],
          [COL.lap, t("struct.dsc.leg_lap")],
          ...(fnd
            ? [[COL.musta, t("struct.dsc.leg_musta")] as [string, string]]
            : []),
        ]} />
      </div>
      {/* PROZA stă aici, nu în desen: în SVG s-ar bate cu raiurile de cotă
          (măsurat pe proiectul 2 — nota înnădirii cădea peste stâlp) */}
      <div className="sg-muted" style={{ fontSize: 11, paddingTop: 6, lineHeight: 1.5 }}>
        {unul ? (
          <>
            {!geo[0].arePet ? (
              <>
                {t("struct.dsc.fara_petrecere")}
              </>
            ) : (
              <>
            {tx("struct.dsc.petrecere", { n: mFmt(geo[0].l0) })}
            {t(geo[0].petInCritica
              ? "struct.dsc.in_critica" : "struct.dsc.peste_critica")}
              </>
            )}
            {geo[0].hG - geo[0].hPl < 0.02 ? (
              <>{t("struct.dsc.fara_grinda", {
                placa: mFmt(geo[0].hPl), liber: mFmt(geo[0].hLib),
              })}</>
            ) : (
              <>{t("struct.dsc.cu_grinda", {
                banda: mFmt(geo[0].hG),
                placa: mFmt(geo[0].hPl),
                liber: mFmt(geo[0].hLib),
              })}</>
            )}
          </>
        ) : (
          <>
            {t("struct.dsc.stiva")}
            {fnd ? (
              <>
                {t("struct.dsc.cu_fundatie", {
                  B: mFmt(fnd.B_m), hT: mFmt(fnd.hTalpa_m),
                  bI: mFmt(fnd.bInima_m), hI: mFmt(fnd.hInima_m),
                })}
                {fnd.ax
                  ? t(fnd.bLocal ? "struct.dsc.axul" : "struct.dsc.axul_global",
                      { ax: fnd.ax })
                  : ""}
                {t("struct.dsc.umplutura_text", { n: mFmt(fnd.hPeste_m) })}
                {fnd.areSubsol
                  ? tx("struct.dsc.subsol", {
                      df: mFmt(fnd.Df_m), sus: mFmt(fnd.hPardoseala_m),
                      dext: mFmt(fnd.Dext_m),
                    })
                  : null}
              </>
            ) : null}
            {t("struct.dsc.fiecare_nivel")}
          </>
        )}
      </div>
      {(!cuEtrieri || coteSarite) && (
        <div className="sg-muted" style={{ fontSize: 11, paddingTop: 5 }}>
          {t("struct.dsc.la_scara", {
            ce: (!cuEtrieri ? t("struct.dsc.etrierii") : "") +
                (!cuEtrieri && coteSarite ? t("struct.dsc.si") : "") +
                (coteSarite ? t("struct.dsc.cotele") : ""),
          })}
        </div>
      )}
      {estimat && (
        <div className="sg-muted" style={{ fontSize: 11, paddingTop: 5 }}>
          {t("struct.dsc.estimat")}
        </div>
      )}
      {cuFundatie && !fundatie && (
        <div className="sg-muted" style={{ fontSize: 11, paddingTop: 5 }}>
          {t("struct.dsc.fara_fundatie")}
        </div>
      )}
    </div>
  );
}

// felia de niveluri cerută, dar cu cotele absolute ale stivei întregi
function geoNiveruriSelectate(
  toate: NivelStalp[],
  selectate: NivelStalp[]
): Geo[] {
  const vrute = new Set(selectate.map((n) => n.id_plan));
  return geoNiveluri(toate).filter((g) => vrute.has(g.n.id_plan));
}
